import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsTresorerie, useComptesTresorerie, useDossierTresorerie, usePiecesJointes, type PosteSaisie, type TransmissionSaisie } from '@/api/hooks';
import { LIBELLE_CATEGORIE_RETOUR, LIBELLE_TYPE_PIECE, LIBELLE_VALIDATEUR, type CategorieRetour, type DetailDossier, type ModePaiement, type ResumeCompte, type Validateur } from '@/api/types';
import { Chargement, CircuitTimeline, ConfirmationDialog, ErreurBloc, PageHeader } from '@/components/common';
import { HistoriqueListe } from '@/components/courrier';
import { PiecesJointes } from '@/components/pieces-jointes';
import { CarteCompte, DelaiBadge, Drapeaux, ModeBadge, StatutDossierBadge, decalerSemaine, libelleSemaine, semaineCourante } from '@/components/tresorerie';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr, montant } from '@/lib/utils';

type Dialogue = 'retourner' | 'reprendre' | 'transmettre' | 'reporter' | 'renvoyer' | 'poster' | 'validation' | 'rejetBanque' | 'confirmer' | 'payerEspeces' | 'rapprocher' | 'demanderAnnulation' | 'decisionAnnulation' | null;

const semainesProposees = (courante: string, ...autres: string[]): string[] => [...new Set([...Array.from({ length: 9 }, (_, i) => decalerSemaine(courante, i)), ...autres])].sort();

// ------------------------------------------------------------------ dialogues spécialisés
function DialogueRetour({ ouvert, onOpenChange, onConfirmer, chargement }: { ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (categorie: CategorieRetour, motif: string) => Promise<unknown>; chargement: boolean }) {
  const [categorie, setCategorie] = useState<CategorieRetour>('PIECES_MANQUANTES');
  const [motif, setMotif] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Retourner le FED à l'émetteur</DialogTitle>
          <DialogDescription>L'émetteur et son directeur sont notifiés ; le FED revient en contrôle après correction. Le motif est précis (20 caractères au moins).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="categorie">Catégorie</Label>
            <SelectNatif id="categorie" value={categorie} onChange={(e) => setCategorie(e.target.value as CategorieRetour)}>
              {(Object.keys(LIBELLE_CATEGORIE_RETOUR) as CategorieRetour[]).map((c) => (
                <option key={c} value={c}>
                  {LIBELLE_CATEGORIE_RETOUR[c]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="motifRetour">Motif</Label>
            <Textarea id="motifRetour" rows={4} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ce qui manque ou ce qui est incohérent, et ce que l'émetteur doit faire." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant="destructive" onClick={() => onConfirmer(categorie, motif.trim())} disabled={chargement || motif.trim().length < 20}>
            Retourner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueTransmission({ d, comptes, ouvert, onOpenChange, onConfirmer, chargement }: { d: DetailDossier; comptes: ResumeCompte[]; ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (corps: TransmissionSaisie) => Promise<unknown>; chargement: boolean }) {
  const dossier = d.dossier;
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => Object.fromEntries(d.checklistLibelles.map((c) => [c.cle, Boolean(dossier.checklist[c.cle])])));
  const [mode, setMode] = useState<ModePaiement>(dossier.mode ?? (dossier.fed.montantUsd <= 2500 && dossier.fed.nature === 'PERDIEMS' ? 'ESPECES' : 'VIREMENT'));
  const [compteId, setCompteId] = useState(dossier.compte?.id ?? '');
  const [tresorierId, setTresorierId] = useState(dossier.tresorier?.id ?? '');
  const [semaine, setSemaine] = useState(dossier.semaine);
  const [motifReport, setMotifReport] = useState('');
  const [justification, setJustification] = useState(dossier.drapeaux.justificationHorsBudget ?? '');
  const [doublonConfirme, setDoublonConfirme] = useState(dossier.drapeaux.doublonConfirme);
  const [derogation, setDerogation] = useState('');
  const banques = comptes.filter((c) => c.type === 'BANQUE' && c.actif);
  const caisse = comptes.find((c) => c.type === 'CAISSE' && c.actif) ?? null;
  const compte = mode === 'ESPECES' ? caisse : (banques.find((c) => c.id === compteId) ?? null);
  const toutCoche = d.checklistLibelles.every((c) => checklist[c.cle]);
  const sousMinimum = compte ? compte.soldePrevisionnel - dossier.fed.montantUsd < compte.soldeMinimum : false;
  const reportSansMotif = semaine !== dossier.semaine && motifReport.trim().length < 5;
  const confirmer = () =>
    onConfirmer({
      checklist,
      mode,
      compteId: mode === 'VIREMENT' ? compteId || undefined : undefined,
      tresorierId: tresorierId || undefined,
      semaine: semaine !== dossier.semaine ? semaine : undefined,
      motifReport: semaine !== dossier.semaine ? motifReport.trim() : undefined,
      justificationHorsBudget: dossier.drapeaux.horsBudget ? justification.trim() || undefined : undefined,
      doublonConfirme: dossier.drapeaux.doublonPossible ? doublonConfirme : undefined,
      derogationSolde: sousMinimum ? derogation.trim() || undefined : undefined,
    });
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Transmettre au paiement</DialogTitle>
          <DialogDescription>Contrôle de conformité (RG-01), mode et compte de paiement, chargé du paiement, semaine du plan de décaissement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Checklist de conformité</h3>
            {d.checklistLibelles.map((c) => (
              <label key={c.cle} className="flex items-start gap-2 text-sm">
                <Checkbox checked={Boolean(checklist[c.cle])} onCheckedChange={(x) => setChecklist({ ...checklist, [c.cle]: x === true })} className="mt-0.5" /> {c.libelle}
              </label>
            ))}
            <Button type="button" variant="ghost" size="sm" onClick={() => setChecklist(Object.fromEntries(d.checklistLibelles.map((c) => [c.cle, true])))}>
              Tout cocher
            </Button>
          </section>
          {dossier.drapeaux.horsBudget && (
            <div className="space-y-1.5">
              <Label htmlFor="justification">FED hors budget : justification écrite (RG-02)</Label>
              <Textarea id="justification" rows={2} value={justification} onChange={(e) => setJustification(e.target.value)} />
            </div>
          )}
          {dossier.drapeaux.doublonPossible && (
            <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <Checkbox checked={doublonConfirme} onCheckedChange={(x) => setDoublonConfirme(x === true)} className="mt-0.5" />
              <span>
                Doublon possible avec {dossier.drapeaux.doublonPossible.numero ?? 'un autre FED'} ({montant(dossier.drapeaux.doublonPossible.montant, true)}) : je confirme qu'il ne s'agit pas d'un doublon (RG-04).
              </span>
            </label>
          )}
          <section className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="mode">Mode de paiement</Label>
              <SelectNatif id="mode" value={mode} onChange={(e) => setMode(e.target.value as ModePaiement)}>
                <option value="VIREMENT">Virement bancaire</option>
                <option value="ESPECES">Espèces (caisse)</option>
              </SelectNatif>
            </div>
            {mode === 'VIREMENT' ? (
              <div className="space-y-1.5">
                <Label htmlFor="compte">Compte FONAREV débité</Label>
                <SelectNatif id="compte" value={compteId} onChange={(e) => { setCompteId(e.target.value); setTresorierId(''); }}>
                  <option value="">Choisir</option>
                  {banques.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.banque} · {c.code} · prévisionnel {montant(c.soldePrevisionnel)}
                    </option>
                  ))}
                </SelectNatif>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Caisse</Label>
                <div className="text-sm">{caisse ? `${caisse.banque} · disponible ${montant(caisse.soldeDisponible, true)}${caisse.plafondOperation !== null ? ` · plafond ${montant(caisse.plafondOperation)}` : ''}` : 'Aucune caisse paramétrée.'}</div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="tresorier">{mode === 'ESPECES' ? 'Caissier' : 'Trésorier'} chargé du paiement</Label>
              <SelectNatif id="tresorier" value={tresorierId} onChange={(e) => setTresorierId(e.target.value)}>
                <option value="">Automatique (affecté, le moins chargé)</option>
                {(compte?.tresoriers ?? []).map((t) => (
                  <option key={t.agentId} value={t.agentId}>
                    {t.nom}
                    {t.suppleant ? ' (suppléant)' : ''}
                  </option>
                ))}
              </SelectNatif>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="semaine">Semaine du plan</Label>
              <SelectNatif id="semaine" value={semaine} onChange={(e) => setSemaine(e.target.value)}>
                {semainesProposees(semaineCourante(), dossier.semaine).map((s) => (
                  <option key={s} value={s}>
                    {libelleSemaine(s)}
                    {s === dossier.semaine ? ' (proposée)' : ''}
                  </option>
                ))}
              </SelectNatif>
            </div>
            {semaine !== dossier.semaine && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="motifReport">Motif du report</Label>
                <Input id="motifReport" value={motifReport} onChange={(e) => setMotifReport(e.target.value)} />
              </div>
            )}
          </section>
          {sousMinimum && compte && (
            <div className="space-y-1.5 rounded-md border border-red-300 bg-red-50 p-3">
              <Label htmlFor="derogation">
                Le solde prévisionnel de {compte.code} passerait sous son minimum ({montant(compte.soldeMinimum)}) : dérogation motivée (RG-06)
              </Label>
              <Textarea id="derogation" rows={2} value={derogation} onChange={(e) => setDerogation(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={confirmer} disabled={chargement || !toutCoche || (mode === 'VIREMENT' && !compteId) || reportSansMotif}>
            Transmettre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueReport({ semaineActuelle, ouvert, onOpenChange, onConfirmer, chargement }: { semaineActuelle: string; ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (semaine: string, motif: string) => Promise<unknown>; chargement: boolean }) {
  const [semaine, setSemaine] = useState(decalerSemaine(semaineActuelle, 1));
  const [motif, setMotif] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reporter à une autre semaine</DialogTitle>
          <DialogDescription>Le report est tracé avec son motif ; l'émetteur est informé. Au-delà de deux reports, le Directeur financier est alerté (RG-08).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="semaineReport">Nouvelle semaine</Label>
            <SelectNatif id="semaineReport" value={semaine} onChange={(e) => setSemaine(e.target.value)}>
              {semainesProposees(semaineCourante(), semaineActuelle)
                .filter((s) => s !== semaineActuelle)
                .map((s) => (
                  <option key={s} value={s}>
                    {libelleSemaine(s)}
                  </option>
                ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="motifReport2">Motif</Label>
            <Textarea id="motifReport2" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer(semaine, motif.trim())} disabled={chargement || motif.trim().length < 5}>
            Reporter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialoguePoste({ d, ouvert, onOpenChange, onConfirmer, chargement }: { d: DetailDossier; ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (corps: PosteSaisie) => Promise<unknown>; chargement: boolean }) {
  const p = d.dossier.paiement;
  const [v, setV] = useState<PosteSaisie>({ compteBeneficiaire: { banque: p?.compteBeneficiaire?.banque ?? '', numero: p?.compteBeneficiaire?.numero ?? '', intitule: p?.compteBeneficiaire?.intitule ?? d.dossier.fed.beneficiaire ?? '', swift: p?.compteBeneficiaire?.swift ?? '' }, referenceFacture: p?.referenceFacture ?? '', libelle: p?.libelle ?? `FED ${d.dossier.fed.numero ?? ''} – ${d.dossier.fed.objet}`.slice(0, 200), numeroOrdre: '' });
  const b = v.compteBeneficiaire;
  const majB = (patch: Partial<PosteSaisie['compteBeneficiaire']>) => setV({ ...v, compteBeneficiaire: { ...b, ...patch } });
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Virement posté sur la plateforme bancaire</DialogTitle>
          <DialogDescription>Saisissez le virement sur la plateforme de {d.dossier.compte?.banque ?? 'la banque'} puis reportez ici les données ; le numéro d'ordre est obligatoire (RG-10). Le FED passe en « liquidé ».</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="bBanque">Banque du bénéficiaire</Label>
            <Input id="bBanque" value={b.banque} onChange={(e) => majB({ banque: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bNumero">Numéro de compte</Label>
            <Input id="bNumero" value={b.numero} onChange={(e) => majB({ numero: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bIntitule">Intitulé du compte</Label>
            <Input id="bIntitule" value={b.intitule} onChange={(e) => majB({ intitule: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bSwift">Code SWIFT (facultatif)</Label>
            <Input id="bSwift" value={b.swift ?? ''} onChange={(e) => majB({ swift: e.target.value })} maxLength={11} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="refFacture">Référence de la facture (facultatif)</Label>
            <Input id="refFacture" value={v.referenceFacture ?? ''} onChange={(e) => setV({ ...v, referenceFacture: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="numeroOrdre">Numéro d'ordre de la plateforme</Label>
            <Input id="numeroOrdre" value={v.numeroOrdre} onChange={(e) => setV({ ...v, numeroOrdre: e.target.value })} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="libelle">Libellé du virement</Label>
            <Input id="libelle" value={v.libelle ?? ''} onChange={(e) => setV({ ...v, libelle: e.target.value })} maxLength={200} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer({ ...v, compteBeneficiaire: { ...b, swift: b.swift?.trim() || undefined }, referenceFacture: v.referenceFacture?.trim() || undefined, libelle: v.libelle?.trim() || undefined, numeroOrdre: v.numeroOrdre.trim() })} disabled={chargement || b.banque.trim().length < 2 || b.numero.trim().length < 4 || b.intitule.trim().length < 2 || v.numeroOrdre.trim().length < 3}>
            Enregistrer le virement posté
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueValidation({ d, ouvert, onOpenChange, onConfirmer, chargement }: { d: DetailDossier; ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (validateur: Validateur, dateHeure: string) => Promise<unknown>; chargement: boolean }) {
  const restants = d.dossier.validationsRequises.filter((v) => !d.dossier.validations.some((x) => x.validateur === v));
  const [validateur, setValidateur] = useState<Validateur>(restants[0] ?? 'DF');
  const [dateHeure, setDateHeure] = useState(new Date().toISOString().slice(0, 16));
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Validation constatée sur la plateforme bancaire</DialogTitle>
          <DialogDescription>Validateurs requis pour ce montant : {d.dossier.validationsRequises.map((v) => LIBELLE_VALIDATEUR[v]).join(', ')} (RG-11). Chaque validation est reportée avec la date et l'heure affichées par la plateforme.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="validateur">Validateur</Label>
            <SelectNatif id="validateur" value={validateur} onChange={(e) => setValidateur(e.target.value as Validateur)}>
              {restants.map((v) => (
                <option key={v} value={v}>
                  {LIBELLE_VALIDATEUR[v]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dateHeure">Date et heure (plateforme)</Label>
            <Input id="dateHeure" type="datetime-local" value={dateHeure} onChange={(e) => setDateHeure(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer(validateur, new Date(dateHeure).toISOString())} disabled={chargement || !restants.length || !dateHeure}>
            Enregistrer la validation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueConfirmation({ d, ouvert, onOpenChange, onConfirmer, chargement }: { d: DetailDossier; ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (dateValeur: string, referenceDebit: string, pieceId?: string) => Promise<unknown>; chargement: boolean }) {
  const pieces = usePiecesJointes('FED', d.dossier.fed.id);
  const avis = (pieces.data?.pieces ?? []).filter((p) => p.type === 'AVIS_DEBIT' && !p.remplacee);
  const [dateValeur, setDateValeur] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [pieceId, setPieceId] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmer le paiement</DialogTitle>
          <DialogDescription>Le virement est exécuté par la banque : date de valeur, référence de débit et avis de débit joint au FED (type « Avis de débit bancaire »). Le FED passe en « payé » et le compte est mouvementé.</DialogDescription>
        </DialogHeader>
        {avis.length === 0 && (
          <Alert variant="destructive">
            <AlertTitle>Aucun avis de débit joint</AlertTitle>
            <AlertDescription>Déposez d'abord l'avis de débit dans les pièces jointes du dossier (type « Avis de débit bancaire »), puis confirmez.</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="dateValeur">Date de valeur</Label>
            <Input id="dateValeur" type="date" value={dateValeur} onChange={(e) => setDateValeur(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="refDebit">Référence de débit</Label>
            <Input id="refDebit" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="avis">Avis de débit</Label>
            <SelectNatif id="avis" value={pieceId} onChange={(e) => setPieceId(e.target.value)}>
              <option value="">Dernier avis de débit joint</option>
              {avis.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nomFichier}
                </option>
              ))}
            </SelectNatif>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer(dateValeur, reference.trim(), pieceId || undefined)} disabled={chargement || !avis.length || !dateValeur || reference.trim().length < 3}>
            Confirmer le paiement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueEspeces({ d, ouvert, onOpenChange, onConfirmer, chargement }: { d: DetailDossier; ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (numeroBon: string, beneficiaire: { nom: string; typePiece: string; numeroPiece: string }, pieceId?: string) => Promise<unknown>; chargement: boolean }) {
  const pieces = usePiecesJointes('FED', d.dossier.fed.id);
  const recus = (pieces.data?.pieces ?? []).filter((p) => p.type === 'RECU_CAISSE' && !p.remplacee);
  const [numeroBon, setNumeroBon] = useState('');
  const [nom, setNom] = useState(d.dossier.fed.beneficiaire ?? '');
  const [typePiece, setTypePiece] = useState("Carte d'électeur");
  const [numeroPiece, setNumeroPiece] = useState('');
  const [pieceId, setPieceId] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Paiement en espèces</DialogTitle>
          <DialogDescription>Bon de caisse, identité du bénéficiaire vérifiée sur une pièce officielle, reçu signé numérisé et joint au FED (type « Reçu de caisse signé »).</DialogDescription>
        </DialogHeader>
        {recus.length === 0 && (
          <Alert variant="destructive">
            <AlertTitle>Aucun reçu de caisse joint</AlertTitle>
            <AlertDescription>Déposez d'abord le reçu signé dans les pièces jointes du dossier (type « Reçu de caisse signé »).</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="numeroBon">Numéro du bon de caisse</Label>
            <Input id="numeroBon" value={numeroBon} onChange={(e) => setNumeroBon(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nomBenef">Bénéficiaire (nom)</Label>
            <Input id="nomBenef" value={nom} onChange={(e) => setNom(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="typePiece">Pièce d'identité</Label>
            <Input id="typePiece" value={typePiece} onChange={(e) => setTypePiece(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="numeroPiece">Numéro de la pièce</Label>
            <Input id="numeroPiece" value={numeroPiece} onChange={(e) => setNumeroPiece(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="recu">Reçu signé</Label>
            <SelectNatif id="recu" value={pieceId} onChange={(e) => setPieceId(e.target.value)}>
              <option value="">Dernier reçu joint</option>
              {recus.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nomFichier}
                </option>
              ))}
            </SelectNatif>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer(numeroBon.trim(), { nom: nom.trim(), typePiece: typePiece.trim(), numeroPiece: numeroPiece.trim() }, pieceId || undefined)} disabled={chargement || !recus.length || !numeroBon.trim() || nom.trim().length < 2 || typePiece.trim().length < 2 || numeroPiece.trim().length < 2}>
            Enregistrer le paiement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueRapprochement({ d, ouvert, onOpenChange, onConfirmer, chargement }: { d: DetailDossier; ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (ligne: string, montant: number) => Promise<unknown>; chargement: boolean }) {
  const [ligne, setLigne] = useState('');
  const [m, setM] = useState(String(d.dossier.fed.montantUsd));
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rapprochement bancaire</DialogTitle>
          <DialogDescription>Ligne du relevé (ou de l'arrêté de caisse) correspondant au paiement ; le montant doit être égal au paiement.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ligneReleve">Ligne du relevé</Label>
            <Input id="ligneReleve" value={ligne} onChange={(e) => setLigne(e.target.value)} placeholder="Référence ou libellé sur le relevé" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="montantReleve">Montant (USD)</Label>
            <Input id="montantReleve" type="number" step="0.01" value={m} onChange={(e) => setM(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer(ligne.trim(), Number(m))} disabled={chargement || !ligne.trim() || m === ''}>
            Rapprocher
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueDecisionAnnulation({ ouvert, onOpenChange, onConfirmer, chargement }: { ouvert: boolean; onOpenChange: (o: boolean) => void; onConfirmer: (decision: 'VALIDER' | 'REJETER', motif?: string) => Promise<unknown>; chargement: boolean }) {
  const [decision, setDecision] = useState<'VALIDER' | 'REJETER'>('VALIDER');
  const [motif, setMotif] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Décision sur la demande d'annulation</DialogTitle>
          <DialogDescription>Approuvée, l'annulation retire le FED de la trésorerie et le marque annulé ; refusée, le dossier reprend son cours.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={decision === 'VALIDER' ? 'default' : 'outline'} onClick={() => setDecision('VALIDER')}>
            Approuver l'annulation
          </Button>
          <Button type="button" size="sm" variant={decision === 'REJETER' ? 'default' : 'outline'} onClick={() => setDecision('REJETER')}>
            Refuser
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="motifDecision">{decision === 'REJETER' ? 'Motif du refus' : 'Commentaire (facultatif)'}</Label>
          <Textarea id="motifDecision" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant={decision === 'VALIDER' ? 'destructive' : 'default'} onClick={() => onConfirmer(decision, motif.trim() || undefined)} disabled={chargement || (decision === 'REJETER' && motif.trim().length < 5)}>
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ page
/** Traitement d'un FED en trésorerie (RT-3, TR-2, CA-2) : contrôle, transmission, paiement, validations, confirmation, rapprochement, annulation. */
export function DossierDetail() {
  const { id } = useParams();
  const { data, isLoading, error } = useDossierTresorerie(id);
  const comptes = useComptesTresorerie();
  const actions = useActionsTresorerie();
  const [dialogue, setDialogue] = useState<Dialogue>(null);

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { dossier: d, actions: a } = data;
  const fed = d.fed;
  const chargement = Object.values(actions).some((m) => m.isPending);
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      const c = controlesDe(e);
      toast.error(e instanceof Error ? e.message : 'Action impossible.', { description: c.length ? c.map((x) => x.message).join(' · ') : undefined });
    }
  };
  const typesPieces = d.mode === 'ESPECES' ? (['RECU_CAISSE', 'JUSTIFICATIF', 'AUTRE'] as const) : (['AVIS_DEBIT', 'FACTURE', 'JUSTIFICATIF', 'AUTRE'] as const);
  const validationsRestantes = d.validationsRequises.filter((v) => !d.validations.some((x) => x.validateur === v));

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/tresorerie/dossiers" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> FED en trésorerie
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {fed.numero ?? 'FED'} <StatutDossierBadge statut={d.statut} /> <ModeBadge mode={d.mode} />
          </span>
        }
        sousTitre={
          <>
            {fed.objet} · {fed.beneficiaire ?? '—'} · {fed.structureCode} · émis par {fed.emetteur?.nom ?? '—'} · validé le {dateHeureFr(d.dateValidation)}
            <Link to={`/fed/${fed.id}`} className="ml-2 inline-flex items-center gap-1 text-primary hover:underline">
              Ouvrir le FED <ExternalLink className="size-3" />
            </Link>
          </>
        }
        actions={
          <>
            {a.prendreEnCharge && <Button onClick={() => executer(() => actions.prendreEnCharge.mutateAsync(d.id), 'Contrôle ouvert.')}>Prendre en charge</Button>}
            {a.transmettre && <Button onClick={() => setDialogue('transmettre')}>Transmettre au paiement</Button>}
            {a.retourner && (
              <Button variant="outline" onClick={() => setDialogue('retourner')}>
                Retourner à l'émetteur
              </Button>
            )}
            {a.reprendre && <Button onClick={() => setDialogue('reprendre')}>Corrigé : renvoyer en contrôle</Button>}
            {a.reporter && (
              <Button variant="outline" onClick={() => setDialogue('reporter')}>
                Reporter
              </Button>
            )}
            {a.preparer && <Button onClick={() => executer(() => actions.preparer.mutateAsync(d.id), 'Paiement pris en charge.')}>Préparer le paiement</Button>}
            {a.renvoyer && (
              <Button variant="outline" onClick={() => setDialogue('renvoyer')}>
                Renvoyer au responsable
              </Button>
            )}
            {a.poster && <Button onClick={() => setDialogue('poster')}>Virement posté</Button>}
            {a.enregistrerValidation && <Button onClick={() => setDialogue('validation')}>Validation constatée</Button>}
            {a.rejetBanque && (
              <Button variant="outline" onClick={() => setDialogue('rejetBanque')}>
                Rejet en banque
              </Button>
            )}
            {a.confirmer && <Button onClick={() => setDialogue('confirmer')}>Confirmer le paiement</Button>}
            {a.payerEspeces && <Button onClick={() => setDialogue('payerEspeces')}>Payer en espèces</Button>}
            {a.rapprocher && <Button onClick={() => setDialogue('rapprocher')}>Rapprocher</Button>}
            {a.demanderAnnulation && (
              <Button variant="ghost" onClick={() => setDialogue('demanderAnnulation')}>
                Demander l'annulation
              </Button>
            )}
            {a.approuverAnnulation && (
              <Button variant="destructive" onClick={() => setDialogue('decisionAnnulation')}>
                Décider l'annulation
              </Button>
            )}
          </>
        }
      />

      {d.statut === 'SUSPENDU' && (
        <Alert variant="destructive">
          <AlertTitle>FED suspendu par une note explicative</AlertTitle>
          <AlertDescription>Le dossier reprendra en « {d.statutAvantSuspension ?? 'validé'} » à l'autorisation de la note.</AlertDescription>
        </Alert>
      )}
      {d.statut === 'RETOURNE' && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTitle>Retourné à l'émetteur · {d.categorieRetour ? LIBELLE_CATEGORIE_RETOUR[d.categorieRetour] : ''}</AlertTitle>
          <AlertDescription>{d.motifRetour}</AlertDescription>
        </Alert>
      )}
      {d.annulation && !d.annulation.approuveeParId && d.statut !== 'ANNULE' && (
        <Alert>
          <AlertTitle>Demande d'annulation en attente du Directeur financier</AlertTitle>
          <AlertDescription>
            {d.annulation.demandeParNom}, le {dateHeureFr(d.annulation.date)} : {d.annulation.motif}
          </AlertDescription>
        </Alert>
      )}
      {d.paiement?.rejets.length ? (
        <Alert variant="destructive">
          <AlertTitle>Rejet(s) en banque</AlertTitle>
          <AlertDescription>
            {d.paiement.rejets.map((r, i) => (
              <div key={i}>
                {dateHeureFr(r.date)} : {r.motif}
              </div>
            ))}
          </AlertDescription>
        </Alert>
      ) : null}
      {d.drapeaux.doublonPossible && !d.drapeaux.doublonConfirme && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTitle>Doublon possible</AlertTitle>
          <AlertDescription>
            Même bénéficiaire et même montant que {d.drapeaux.doublonPossible.numero ?? 'un autre FED'} ({montant(d.drapeaux.doublonPossible.montant, true)}). Le responsable confirme à la transmission qu'il ne s'agit pas d'un doublon.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Paiement</CardTitle>
              <CardDescription>
                <Drapeaux d={d} />
              </CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <dt className="text-xs text-muted-foreground">Montant</dt>
                  <dd className="num text-lg font-semibold">{montant(fed.montantUsd, true)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Semaine du plan</dt>
                  <dd>
                    {libelleSemaine(d.semaine)}
                    {d.dateProposee && <div className="text-xs text-muted-foreground">date proposée par l'émetteur : {dateFr(d.dateProposee)}</div>}
                    {d.semaine !== d.semaineInitiale && <div className="text-xs text-muted-foreground">initialement {d.semaineInitiale}</div>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Délai dans ce statut</dt>
                  <dd>
                    <DelaiBadge delai={d.delai} /> <span className="text-xs text-muted-foreground">depuis le {dateHeureFr(d.dateStatut)}</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Compte de paiement</dt>
                  <dd>{d.compte ? `${d.compte.banque} · ${d.compte.code}` : '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{d.mode === 'ESPECES' ? 'Caissier' : 'Trésorier'}</dt>
                  <dd>{d.tresorier?.nom ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Ligne budgétaire</dt>
                  <dd>{fed.ligneBudgetaire ? `${fed.ligneBudgetaire.code} · ${fed.ligneBudgetaire.libelle}` : '—'}</dd>
                </div>
                {d.mode === 'VIREMENT' && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <dt className="text-xs text-muted-foreground">Validateurs sur la plateforme bancaire (RG-11)</dt>
                    <dd className="flex flex-wrap gap-2 pt-1">
                      {d.validationsRequises.map((v) => {
                        const faite = d.validations.find((x) => x.validateur === v);
                        return (
                          <Badge key={v} variant="outline" className={cn(faite ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'text-muted-foreground')}>
                            {LIBELLE_VALIDATEUR[v]}
                            {faite ? ` · ${dateHeureFr(faite.dateHeure)}` : ' · en attente'}
                          </Badge>
                        );
                      })}
                    </dd>
                  </div>
                )}
              </dl>
              {d.reports.length > 0 && (
                <div className="mt-4 text-sm">
                  <div className="mb-1 font-medium">Reports du plan</div>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {d.reports.map((r, i) => (
                      <li key={i}>
                        {dateHeureFr(r.date)} · {r.de} → {r.vers} · {r.auteurNom}
                        {r.parDf ? ' (DF)' : ''} : {r.motif}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {d.paiement && (d.paiement.compteBeneficiaire || d.paiement.numeroOrdre) && (
            <Card>
              <CardHeader>
                <CardTitle>Virement</CardTitle>
                <CardDescription>
                  {d.paiement.dateSaisie ? `Posté le ${dateHeureFr(d.paiement.dateSaisie)}` : ''}
                  {d.paiement.numeroOrdre ? ` · ordre n° ${d.paiement.numeroOrdre}` : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-muted-foreground">Compte bénéficiaire</dt>
                    <dd>
                      {d.paiement.compteBeneficiaire ? `${d.paiement.compteBeneficiaire.intitule} · ${d.paiement.compteBeneficiaire.banque} · ${d.paiement.compteBeneficiaire.numero}${d.paiement.compteBeneficiaire.swift ? ` · ${d.paiement.compteBeneficiaire.swift}` : ''}` : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Libellé</dt>
                    <dd>{d.paiement.libelle ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Référence de la facture</dt>
                    <dd>{d.paiement.referenceFacture ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Exécution</dt>
                    <dd>{d.paiement.dateValeur ? `date de valeur ${dateFr(d.paiement.dateValeur)} · référence ${d.paiement.referenceDebit ?? ''}` : 'en attente de confirmation'}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          )}
          {d.especes?.numeroBon && (
            <Card>
              <CardHeader>
                <CardTitle>Paiement en espèces</CardTitle>
                <CardDescription>Bon de caisse n° {d.especes.numeroBon}</CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                {d.especes.beneficiaire ? `${d.especes.beneficiaire.nom} · ${d.especes.beneficiaire.typePiece} n° ${d.especes.beneficiaire.numeroPiece}` : '—'}
                {d.datePaiement && <div className="text-xs text-muted-foreground">payé le {dateHeureFr(d.datePaiement)}</div>}
              </CardContent>
            </Card>
          )}
          {d.rapprochement && (
            <Card>
              <CardHeader>
                <CardTitle>Rapprochement</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                Ligne « {d.rapprochement.ligneReleve} » · {dateHeureFr(d.rapprochement.date)}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Lignes du FED</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Bénéficiaire</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.lignes.map((l) => (
                    <TableRow key={l.ordre}>
                      <TableCell>{l.ordre}</TableCell>
                      <TableCell>{l.description}</TableCell>
                      <TableCell>{l.beneficiaire}</TableCell>
                      <TableCell className="num text-right">{montant(l.montant, true)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contrôle de conformité</CardTitle>
              <CardDescription>Les sept points sont cochés par le responsable de la trésorerie à la transmission (RG-01).</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {data.checklistLibelles.map((c) => (
                  <li key={c.cle} className={cn('flex items-center gap-2', d.checklist[c.cle] ? 'text-emerald-800' : 'text-muted-foreground')}>
                    <span className={cn('inline-block size-2 rounded-full', d.checklist[c.cle] ? 'bg-emerald-500' : 'bg-muted-foreground/40')} /> {c.libelle}
                  </li>
                ))}
              </ul>
              {d.drapeaux.justificationHorsBudget && <div className="mt-3 rounded bg-muted px-2 py-1 text-sm">Hors budget : {d.drapeaux.justificationHorsBudget}</div>}
            </CardContent>
          </Card>

          <PiecesJointes objetType="FED" objetId={fed.id} typesSuggeres={[...typesPieces]} description={d.mode === 'ESPECES' ? `Le reçu de caisse signé (« ${LIBELLE_TYPE_PIECE.RECU_CAISSE} ») est joint avant d'enregistrer le paiement.` : `L'avis de débit (« ${LIBELLE_TYPE_PIECE.AVIS_DEBIT} ») est joint avant de confirmer le paiement.`} />
        </div>

        <div className="space-y-6">
          {data.compte && <CarteCompte c={data.compte} />}
          {data.budget && (
            <Card>
              <CardHeader>
                <CardTitle>Ligne budgétaire</CardTitle>
                <CardDescription>{data.budget.code}</CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Disponible</span>
                  <span className={cn('num font-medium', data.budget.disponible < 0 && 'text-red-700')}>{montant(data.budget.disponible, true)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Disponible prévisionnel</span>
                  <span className={cn('num', data.budget.disponiblePrevisionnel < 0 && 'text-amber-700')}>{montant(data.budget.disponiblePrevisionnel, true)}</span>
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation du FED</CardTitle>
            </CardHeader>
            <CardContent>
              <CircuitTimeline circuit={data.circuit} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Historique en trésorerie</CardTitle>
            </CardHeader>
            <CardContent>
              <HistoriqueListe historique={d.historique} />
            </CardContent>
          </Card>
        </div>
      </div>

      {dialogue === 'retourner' && <DialogueRetour ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(categorie, motif) => executer(() => actions.retourner.mutateAsync({ id: d.id, categorie, motif }), "FED retourné à l'émetteur.")} />}
      {dialogue === 'transmettre' && <DialogueTransmission d={data} comptes={comptes.data ?? []} ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(corps) => executer(() => actions.transmettre.mutateAsync({ id: d.id, corps }), 'FED transmis au paiement et inscrit au plan.')} />}
      {dialogue === 'reporter' && <DialogueReport semaineActuelle={d.semaine} ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(semaine, motif) => executer(() => actions.reporter.mutateAsync({ id: d.id, semaine, motif }), 'Paiement reporté.')} />}
      {dialogue === 'poster' && <DialoguePoste d={data} ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(corps) => executer(() => actions.poster.mutateAsync({ id: d.id, corps }), 'Virement posté : FED liquidé, validateurs notifiés.')} />}
      {dialogue === 'validation' && <DialogueValidation d={data} ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(validateur, dateHeure) => executer(() => actions.validation.mutateAsync({ id: d.id, validateur, dateHeure }), validationsRestantes.length <= 1 ? 'Dernière validation enregistrée : paiement à confirmer.' : 'Validation enregistrée.')} />}
      {dialogue === 'confirmer' && <DialogueConfirmation d={data} ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(dateValeur, referenceDebit, pieceId) => executer(() => actions.confirmer.mutateAsync({ id: d.id, dateValeur, referenceDebit, pieceId }), 'Paiement confirmé : FED payé.')} />}
      {dialogue === 'payerEspeces' && <DialogueEspeces d={data} ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(numeroBon, beneficiaire, pieceId) => executer(() => actions.payerEspeces.mutateAsync({ id: d.id, numeroBon, beneficiaire, pieceId }), 'Paiement en espèces enregistré.')} />}
      {dialogue === 'rapprocher' && <DialogueRapprochement d={data} ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(ligneReleve, m) => executer(() => actions.rapprocher.mutateAsync({ id: d.id, ligneReleve, montant: m }), 'Paiement rapproché.')} />}
      {dialogue === 'decisionAnnulation' && <DialogueDecisionAnnulation ouvert onOpenChange={(o) => !o && setDialogue(null)} chargement={chargement} onConfirmer={(decision, motif) => executer(() => actions.decisionAnnulation.mutateAsync({ id: d.id, decision, motif }), decision === 'VALIDER' ? 'FED annulé.' : 'Annulation refusée.')} />}
      <ConfirmationDialog ouvert={dialogue === 'reprendre'} onOpenChange={(o) => !o && setDialogue(null)} titre="Correction apportée" description="Décrivez la correction ou la pièce ajoutée ; le dossier revient en contrôle trésorerie." libelle="Renvoyer en contrôle" chargement={chargement} champ={{ label: 'Commentaire', lignes: 3 }} onConfirmer={(commentaire) => executer(() => actions.reprendre.mutateAsync({ id: d.id, commentaire }), 'Dossier renvoyé en contrôle.')} />
      <ConfirmationDialog ouvert={dialogue === 'renvoyer'} onOpenChange={(o) => !o && setDialogue(null)} titre="Renvoyer au responsable" description="Le dossier retourne en contrôle trésorerie avec votre motif." libelle="Renvoyer" chargement={chargement} champ={{ label: 'Motif', lignes: 3 }} onConfirmer={(motif) => executer(() => actions.renvoyer.mutateAsync({ id: d.id, motif }), 'Dossier renvoyé au responsable.')} />
      <ConfirmationDialog ouvert={dialogue === 'rejetBanque'} onOpenChange={(o) => !o && setDialogue(null)} titre="Rejet constaté sur la plateforme" description="Le virement est annulé en banque ; le dossier revient en préparation pour un nouveau virement." libelle="Enregistrer le rejet" chargement={chargement} champ={{ label: 'Motif du rejet', lignes: 3 }} onConfirmer={(motif) => executer(() => actions.rejetBanque.mutateAsync({ id: d.id, motif }), 'Rejet enregistré.')} />
      <ConfirmationDialog ouvert={dialogue === 'demanderAnnulation'} onOpenChange={(o) => !o && setDialogue(null)} titre="Demander l'annulation du paiement" description="Possible avant le poste en banque ; le Directeur financier approuve ou refuse." libelle="Demander" chargement={chargement} champ={{ label: 'Motif', lignes: 3 }} onConfirmer={(motif) => executer(() => actions.demanderAnnulation.mutateAsync({ id: d.id, motif }), "Demande d'annulation transmise au DF.")} />
    </div>
  );
}
